import { memo, useState } from "react";
import { Form, Formik } from "formik";
import classNames from "classnames";
import { Cell, Column, Header, Heading, NumericValue, Table, TableColumns } from "@edgetier/client-components";
import { SubmitOnChange } from "@edgetier/components";
import { InteractionMetric } from "@edgetier/watchtower-types";
import { useInteractionMetricsAvailable } from "hooks-for/metrics/use-interaction-metrics-available";
import EllipsisTooltipText from "components-for/display/ellipsis-tooltip-text";
import Toggle from "components-for/filters/toggle";
import MultipleComparisonTableColumns from "./multiple-comparison-table-columns";
import DownloadComparisonTableButton from "./download-comparison-table-button";
import InteractionMetricSummaryPercentageValue from "./interaction-metric-summary-percentage-value";
import InteractionMetricSummaryValue from "./interaction-metric-summary-value";
import PerformanceIndicator from "./performance-indicator";
import { checkMetricAvailable } from "utilities/check-metric-available";
import { IProps, IComparisonData } from "./comparison-table.types";
import { useComparisonData } from "./comparison-table.utilities";
import {
    EXPERIENCE_SCORE_METRIC_ENABLED,
    GRAMMAR_METRIC_ENABLED,
    SPELLING_METRIC_ENABLED,
} from "constants/feature-flags";
import { roundTo } from "utilities/round-to";
import "./comparison-table.scss";

/**
 * Show are comparison of some entities against various interaction counts, metrics etc.
 * @param props.onRowClick Optional handler when a user clicks a row.
 * @param props.getRowLink Optional function to create a row link
 * @param props.groupBy    Property to group data on.
 * @param props.groups     Rows being analysed/grouped.
 * @returns                Table of data.
 */
const ComparisonTable = <Group extends {}>({
    children,
    className,
    getGroupId,
    groupBy,
    groups,
    groupTitle,
    groupPluralTitle = `${groupTitle}s`,
    headingProperties,
    hasHorizontalScroll,
    getRowLink,
    onRowClick,
    interactionType,
}: IProps<Group>) => {
    const [showPercentage, setShowPercentage] = useState(true);
    const { comparisonData, overallComparisonData, isComparisonDataLoading } = useComparisonData(
        groups,
        getGroupId,
        groupBy
    );

    const { data: interactionMetricsAvailable, isLoading: areInteractionMetricsAvailableLoading } =
        useInteractionMetricsAvailable({ interactionType });

    /**
     * Show or hide the percentage in the table values based on what the user selects.
     * @param values The table filter values.
     */
    const onShowPercentageToggle = (values: { showPercentage: boolean }) => {
        setShowPercentage(values.showPercentage);
    };

    return (
        <div className={classNames("comparison-table__container", className)}>
            {!areInteractionMetricsAvailableLoading && (
                <Table
                    constantWidth={false}
                    className="comparison-table"
                    fixedRows={
                        typeof overallComparisonData === "undefined" || (comparisonData ?? []).length === 1
                            ? []
                            : [overallComparisonData]
                    }
                    getRowKey={({ groupId, isOverall, tagValues }) =>
                        isOverall ? `${groupId}-${(tagValues ?? []).reduce((sum, value) => sum + value, 0)}` : groupId
                    }
                    headerIsSticky
                    hasHorizontalScroll={hasHorizontalScroll}
                    onRowClick={({ groupId, isOverall }) => {
                        // Do nothing rather than using { pointer-events: none } in CSS. Otherwise, you can click a row that's
                        // behind the overall row because the overall row is sticky.
                        if (!isOverall && typeof onRowClick === "function") {
                            return onRowClick(groupId);
                        }
                    }}
                    getRowLink={({ isOverall, groupId }) => {
                        if (!isOverall && typeof getRowLink === "function") {
                            return getRowLink(groupId);
                        }
                    }}
                    rows={
                        typeof comparisonData === "undefined" ||
                        // Avoid showing "No results found" (empty array) while data is still loading.
                        isComparisonDataLoading ||
                        !Array.isArray(groups) ||
                        areInteractionMetricsAvailableLoading
                            ? null
                            : comparisonData
                    }
                >
                    <Header>
                        <Formik initialValues={{ showPercentage }} onSubmit={onShowPercentageToggle}>
                            <Form name="comparison-table-filters">
                                <Toggle name="showPercentage" leftLabel="Show Percentage Deviation" />
                                <SubmitOnChange />
                            </Form>
                        </Formik>
                        <DownloadComparisonTableButton<any>
                            comparisonData={comparisonData}
                            overallComparisonData={overallComparisonData}
                            groupPluralTitle={groupPluralTitle}
                            headingProperties={headingProperties}
                        />
                    </Header>
                    <TableColumns>
                        <Column>
                            <Heading>{groupTitle}</Heading>
                            <Cell<IComparisonData<Group>>>
                                {({ comparisonGroup, isOverall }) =>
                                    isOverall || comparisonGroup === null ? (
                                        <div className="comparison-table__overall">Overall</div>
                                    ) : (
                                        <EllipsisTooltipText>{children(comparisonGroup)}</EllipsisTooltipText>
                                    )
                                }
                            </Cell>
                        </Column>

                        <Column>
                            <Heading className="comparison-table__interaction-count" sortProperty="count">
                                Interaction Count
                            </Heading>
                            <Cell<IComparisonData<Group>>>
                                {({ count, isOverall }) => (
                                    <div>
                                        <NumericValue value={count} />
                                        {!isOverall && (
                                            <>
                                                {" ("}
                                                <NumericValue
                                                    value={
                                                        typeof overallComparisonData?.count === "number" &&
                                                        typeof count === "number"
                                                            ? roundTo(count / overallComparisonData?.count, 2) * 100
                                                            : 0
                                                    }
                                                    options={{ maximumFractionDigits: 0 }}
                                                    unit="%"
                                                />
                                                {")"}
                                            </>
                                        )}
                                    </div>
                                )}
                            </Cell>
                        </Column>

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.HandlingTime) && (
                            <Column>
                                <Heading sortProperty="meanHandlingTimeSeconds">Average Handling Time</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanHandlingTimeSeconds }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanHandlingTimeSeconds ?? null}
                                            negativeIsGood
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanHandlingTimeSeconds}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanHandlingTimeSeconds}
                                                interactionMetric={InteractionMetric.HandlingTime}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.AverageResponseTime) && (
                            <Column>
                                <Heading sortProperty="meanResponseSeconds">Average Response Time</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanResponseSeconds }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanResponseSeconds ?? null}
                                            negativeIsGood
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanResponseSeconds}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanResponseSeconds}
                                                interactionMetric={InteractionMetric.AverageResponseTime}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}
                        {checkMetricAvailable(
                            interactionMetricsAvailable!,
                            InteractionMetric.AgentFirstResponseTime
                        ) && (
                            <Column>
                                <Heading sortProperty="meanAgentFirstResponseTime">
                                    Average Agent First Response Time
                                </Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanAgentFirstResponseTime }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanAgentFirstResponseTime ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanAgentFirstResponseTime}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanAgentFirstResponseTime}
                                                interactionMetric={InteractionMetric.AgentFirstResponseTime}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.NetPromoterScore) && (
                            <Column>
                                <Heading sortProperty="netPromoterScore">NPS</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, netPromoterScore }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.netPromoterScore ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={netPromoterScore}
                                        >
                                            <InteractionMetricSummaryValue
                                                interactionMetric={InteractionMetric.NetPromoterScore}
                                                value={netPromoterScore}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.CustomerSatisfaction) && (
                            <Column>
                                <Heading sortProperty="customerSatisfactionScore">CSAT</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, customerSatisfactionScore }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.customerSatisfactionScore ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={customerSatisfactionScore}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={customerSatisfactionScore}
                                                interactionMetric={InteractionMetric.CustomerSatisfaction}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.QueryResolution) && (
                            <Column>
                                <Heading sortProperty="queryResolutionRate">Resolution Rate</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, queryResolutionRate }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.queryResolutionRate ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={queryResolutionRate}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={queryResolutionRate}
                                                interactionMetric={InteractionMetric.QueryResolution}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.AgentSatisfaction) && (
                            <Column>
                                <Heading sortProperty="meanAgentSatisfaction">Average Agent Satisfaction</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanAgentSatisfaction }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanAgentSatisfaction ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanAgentSatisfaction}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanAgentSatisfaction}
                                                interactionMetric={InteractionMetric.AgentSatisfaction}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}
                        {checkMetricAvailable(interactionMetricsAvailable!, InteractionMetric.AgentScore) && (
                            <Column>
                                <Heading sortProperty="meanAgentScore">Average Agent Score</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanAgentScore }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanAgentScore ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanAgentScore}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanAgentScore}
                                                interactionMetric={InteractionMetric.AgentScore}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {checkMetricAvailable(
                            interactionMetricsAvailable!,
                            InteractionMetric.ExperienceScore,
                            EXPERIENCE_SCORE_METRIC_ENABLED
                        ) && (
                            <Column>
                                <Heading sortProperty="meanExperienceScore">Average Experience Score</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, meanExperienceScore }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.meanExperienceScore ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={meanExperienceScore}
                                        >
                                            <InteractionMetricSummaryValue
                                                value={meanExperienceScore}
                                                interactionMetric={InteractionMetric.ExperienceScore}
                                                isMaybeLoading={isComparisonDataLoading}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        <Column>
                            <Heading sortProperty="percentageFrustration">Frustration</Heading>
                            <Cell<IComparisonData<Group>>>
                                {({ isOverall, percentageFrustration }) => (
                                    <PerformanceIndicator
                                        overallValue={overallComparisonData?.percentageFrustration ?? null}
                                        negativeIsGood
                                        showPercentage={showPercentage && !isOverall}
                                        value={percentageFrustration}
                                    >
                                        <InteractionMetricSummaryPercentageValue
                                            isMaybeLoading={isComparisonDataLoading}
                                            value={percentageFrustration}
                                        />
                                    </PerformanceIndicator>
                                )}
                            </Cell>
                        </Column>

                        <Column>
                            <Heading sortProperty="percentagePraise">Praise</Heading>
                            <Cell<IComparisonData<Group>>>
                                {({ isOverall, percentagePraise }) => (
                                    <PerformanceIndicator
                                        overallValue={overallComparisonData?.percentagePraise ?? null}
                                        showPercentage={showPercentage && !isOverall}
                                        value={percentagePraise}
                                    >
                                        <InteractionMetricSummaryPercentageValue
                                            isMaybeLoading={isComparisonDataLoading}
                                            value={percentagePraise}
                                        />
                                    </PerformanceIndicator>
                                )}
                            </Cell>
                        </Column>

                        {SPELLING_METRIC_ENABLED && (
                            <Column>
                                <Heading sortProperty="percentageSpelling">Spelling</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, percentageSpelling }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.percentageSpelling ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={percentageSpelling}
                                        >
                                            <InteractionMetricSummaryPercentageValue
                                                isMaybeLoading={isComparisonDataLoading}
                                                value={percentageSpelling}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}

                        {GRAMMAR_METRIC_ENABLED && (
                            <Column>
                                <Heading sortProperty="percentageGrammar">Grammar</Heading>
                                <Cell<IComparisonData<Group>>>
                                    {({ isOverall, percentageGrammar }) => (
                                        <PerformanceIndicator
                                            overallValue={overallComparisonData?.percentageGrammar ?? null}
                                            showPercentage={showPercentage && !isOverall}
                                            value={percentageGrammar}
                                        >
                                            <InteractionMetricSummaryPercentageValue
                                                isMaybeLoading={isComparisonDataLoading}
                                                value={percentageGrammar}
                                            />
                                        </PerformanceIndicator>
                                    )}
                                </Cell>
                            </Column>
                        )}
                        <MultipleComparisonTableColumns
                            isComparisonDataLoading={isComparisonDataLoading}
                            showPercentage={showPercentage}
                            overallComparisonData={overallComparisonData}
                        />
                    </TableColumns>
                </Table>
            )}
        </div>
    );
};

export default memo(ComparisonTable) as typeof ComparisonTable;
